import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import TestDeliverabilityTab from 'src/components/SmartCompose/TestDeliverabilityTab';

const TEST_DELIVERABILITY_VIDEO_URL = 'https://player.vimeo.com/video/1177930856';
const MESSAGE_SCORE_VIDEO_URL = '';

const RATING_CONFIG = {
  GREAT: {
    label: 'Great',
    color: '#4caf50',
    bgColor: '#e8f5e9',
  },
  GOOD: {
    label: 'Good',
    color: '#8bc34a',
    bgColor: '#f1f8e9',
  },
  FAIR: {
    label: 'Fair',
    color: '#ff9800',
    bgColor: '#fff3e0',
  },
  NEEDS_WORK: {
    label: 'Needs Work',
    color: '#f44336',
    bgColor: '#ffebee',
  },
};

function SmartComposeModal({
  open,
  onClose,
  loading,
  scoreData,
  error = null,
  sourceType = 'other',
  sourceId = null,
  initialTab = 'messageScore',
  messageBody = '',
  messageBodyRaw = '',
  attachmentFileName = null,
  attachmentSizeBytes = null,
  attachmentBlobName = null,
  attachmentContainer = null,
  canViewTestDeliverability = false,
  defaultCarrier = null,
  unsupportedCarrierName = null,
}) {
  const { user } = useSelector((state) => state?.account || {});
  const [activeTab, setActiveTab] = useState(initialTab);

  const isCanadian = user?.country === 'Canada';
  const showTestDeliverability = canViewTestDeliverability && !isCanadian;

  // Sync activeTab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  const ratingConfig = scoreData
    ? RATING_CONFIG[scoreData.overallRating] || RATING_CONFIG.FAIR
    : null;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const videoURL = activeTab === 'testDeliverability' && showTestDeliverability
    ? TEST_DELIVERABILITY_VIDEO_URL
    : MESSAGE_SCORE_VIDEO_URL;

  const messageScoreContent = (
    <Box sx={{ minWidth: 300, maxWidth: 480 }}>
      {loading && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6,
        }}
        >
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
          gap: 1,
        }}
        >
          <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 40 }} />
          <Typography
            variant="body1"
            sx={{ color: '#5d4037', textAlign: 'center' }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && scoreData && (
        <>
          {/* Overall Rating Badge */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 3,
          }}
          >
            <Chip
              label={`${ratingConfig.label} — ${scoreData.overallScore}/100`}
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                py: 2.5,
                px: 2,
                color: ratingConfig.color,
                backgroundColor: ratingConfig.bgColor,
                border: `1px solid ${ratingConfig.color}`,
              }}
            />
          </Box>

          {/* Disclaimer */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontStyle: 'italic',
              color: 'text.secondary',
              textAlign: 'center',
              mx: 2,
              mt: 1,
              mb: 2,
            }}
          >
            Message Score is a guide, not a guarantee. Carrier filtering
            depends on many factors including your specific links, images,
            sending history, and recipient carrier — some of which
            can&apos;t be predicted.
          </Typography>

          {/* To Improve Section */}
          {scoreData.opportunities.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: '#37474f',
                }}
              >
                To Improve
              </Typography>
              {scoreData.opportunities.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: '#fff8e1',
                  }}
                >
                  <WarningAmberIcon
                    sx={{
                      color: '#ff9800',
                      mr: 1.5,
                      mt: 0.25,
                      fontSize: 20,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: '#5d4037', flexShrink: 1 }}
                  >
                    {item.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Looking Good Section */}
          {scoreData.highlights.length > 0 && (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: '#37474f',
                }}
              >
                Looking Good
              </Typography>
              {scoreData.highlights.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: '#e8f5e9',
                  }}
                >
                  <TaskAltIcon
                    sx={{
                      color: '#4caf50',
                      mr: 1.5,
                      mt: 0.25,
                      fontSize: 20,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: '#1b5e20', flexShrink: 1 }}
                  >
                    {item.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  const content = (
    <Box sx={{ minWidth: 300, maxWidth: 480 }}>
      {showTestDeliverability ? (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ mb: 2 }}
          >
            <Tab label="Message Score" value="messageScore" />
            <Tab label="Test Deliverability" value="testDeliverability" />
          </Tabs>
          {activeTab === 'messageScore' && messageScoreContent}
          {activeTab === 'testDeliverability' && (
            <TestDeliverabilityTab
              open={open}
              messageBody={messageBody}
              messageBodyRaw={messageBodyRaw}
              attachmentFileName={attachmentFileName}
              attachmentSizeBytes={attachmentSizeBytes}
              attachmentBlobName={attachmentBlobName}
              attachmentContainer={attachmentContainer}
              sourceType={sourceType}
              sourceId={sourceId}
              salespersonId={user?.salespersonId}
              messageScore={scoreData?.overallScore || null}
              scoreData={scoreData || null}
              defaultCarrier={defaultCarrier}
              unsupportedCarrierName={unsupportedCarrierName}
            />
          )}
        </>
      ) : (
        messageScoreContent
      )}
    </Box>
  );

  return (
    <ModalDialogMUI6
      isVisible={open}
      onClose={onClose}
      title={(
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
          >
            <AutoAwesomeIcon sx={{ color: '#ff9800' }} />
            Smart Compose
          </Box>
        </Box>
      )}
      content={content}
      closeText="Close"
      buttonsListOrder={['close']}
      modalSize="sm"
      showVideo
      videoURL={videoURL}
      videoTooltip="How to Use Test Deliverability"
    />
  );
}

SmartComposeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  scoreData: PropTypes.shape({
    overallRating: PropTypes.string,
    overallScore: PropTypes.number,
    opportunities: PropTypes.array,
    highlights: PropTypes.array,
  }),
  error: PropTypes.string,
  sourceType: PropTypes.string,
  sourceId: PropTypes.number,
  initialTab: PropTypes.string,
  messageBody: PropTypes.string,
  messageBodyRaw: PropTypes.string,
  attachmentFileName: PropTypes.string,
  attachmentSizeBytes: PropTypes.number,
  attachmentBlobName: PropTypes.string,
  attachmentContainer: PropTypes.string,
  canViewTestDeliverability: PropTypes.bool,
  defaultCarrier: PropTypes.string,
  unsupportedCarrierName: PropTypes.string,
};

export default SmartComposeModal;
